import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Globe, Linkedin, ExternalLink,
  User, FileText, Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { PageLoader } from '@shared/components/PageLoader';
import { connectsAPI } from '@/utils/supabase/client';
import type { CompanyDetailResponse, ConnectsMention, ConnectsRelationship } from '@/types/connects';
import { formatDistanceToNow, format } from 'date-fns';

function MentionRow({ mention }: { mention: ConnectsMention }) {
  const ki = mention.knox_items;
  return (
    <div className="py-3 border-b last:border-0">
      <div className="flex items-center gap-2 mb-1">
        <Badge variant="outline" className="text-xs">{ki?.source ?? 'unknown'}</Badge>
        {ki?.source_channel && <span className="text-xs text-muted-foreground">{ki.source_channel}</span>}
        {ki?.author_name && <span className="text-xs text-muted-foreground">· {ki.author_name}</span>}
        {ki?.occurred_at && (
          <span className="text-xs text-muted-foreground ml-auto">
            {formatDistanceToNow(new Date(ki.occurred_at), { addSuffix: true })}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground line-clamp-3">{mention.excerpt || ki?.content?.slice(0, 200)}</p>
    </div>
  );
}

function RelationshipRow({ rel, subjectId }: { rel: ConnectsRelationship; subjectId: string }) {
  const isFrom = rel.from_id === subjectId;
  const otherType = isFrom ? rel.to_type : rel.from_type;
  const label = rel.kind.replace(/_/g, ' ');
  const confidence = Math.round(rel.confidence * 100);
  return (
    <div className="flex items-center gap-3 py-2 border-b last:border-0">
      <div className="flex items-center gap-2">
        {otherType === 'contact' ? <User className="h-4 w-4 text-muted-foreground" /> : <Building2 className="h-4 w-4 text-muted-foreground" />}
        <span className="text-xs text-muted-foreground">{otherType}</span>
      </div>
      <Badge variant="outline" className="text-xs">{label}</Badge>
      <span className="text-xs text-muted-foreground ml-auto">{confidence}%</span>
    </div>
  );
}

export function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<CompanyDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    connectsAPI.getCompany(id)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <PageLoader />;
  if (error || !data) return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
      <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">{error ?? 'Not found'}</div>
    </div>
  );

  const { company, dossier, contacts, mentions, relationships } = data;

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/connects')} className="mb-3 -ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center shrink-0">
              <Building2 className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight font-serif">{company.name}</h1>
              {company.industry && <p className="text-sm text-muted-foreground">{company.industry}</p>}
              {company.domain && (
                <a href={`https://${company.domain}`} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1 mt-0.5">
                  <Globe className="h-3 w-3" /> {company.domain}
                </a>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:ml-auto">
            {company.is_client && <Badge className="text-xs">Client</Badge>}
            {company.client_stage && <Badge variant="outline" className="text-xs">{company.client_stage}</Badge>}
            {company.tags.map((t) => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4 lg:col-span-1">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm">Details</CardTitle></CardHeader>
            <CardContent className="pt-0 space-y-2">
              {company.website && (
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                  <a href={company.website} target="_blank" rel="noreferrer" className="hover:underline truncate flex items-center gap-1">
                    {company.website} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {company.linkedin_url && (
                <div className="flex items-center gap-2 text-sm">
                  <Linkedin className="h-3.5 w-3.5 text-muted-foreground" />
                  <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="hover:underline truncate flex items-center gap-1">
                    LinkedIn <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {company.description && <p className="text-sm text-muted-foreground">{company.description}</p>}
              <Separator />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Mentions</span><span className="font-medium text-foreground">{company.mention_count}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>First seen</span><span>{format(new Date(company.first_mentioned_at), 'MMM d, yyyy')}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Last seen</span>
                <span>{formatDistanceToNow(new Date(company.last_mentioned_at), { addSuffix: true })}</span>
              </div>
            </CardContent>
          </Card>

          {/* People at this company */}
          {contacts.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm">People ({contacts.length})</CardTitle></CardHeader>
              <CardContent className="pt-0 space-y-1">
                {contacts.map((ct) => (
                  <button
                    key={ct.id}
                    onClick={() => navigate(`/connects/contacts/${ct.id}`)}
                    className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-accent text-left transition-colors"
                  >
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-sm truncate">{ct.display_name}</p>
                      {ct.title && <p className="text-xs text-muted-foreground truncate">{ct.title}</p>}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Dossier */}
          {dossier && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Dossier
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-4">
                {dossier.site && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Site</p>
                    {dossier.site.title && <p className="text-sm font-medium">{dossier.site.title}</p>}
                    {dossier.site.meta_description && <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{dossier.site.meta_description}</p>}
                  </div>
                )}
                {dossier.press && dossier.press.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Recent press</p>
                    <div className="space-y-2">
                      {dossier.press.slice(0, 3).map((p, i) => (
                        <a key={i} href={p.url} target="_blank" rel="noreferrer" className="block hover:bg-accent rounded p-2 -m-2 group">
                          <p className="text-xs font-medium group-hover:underline line-clamp-2">{p.title}</p>
                          {p.publication && <p className="text-xs text-muted-foreground">{p.publication}</p>}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4 lg:col-span-2">
          {relationships.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Relationships ({relationships.length})</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {relationships.map((r) => <RelationshipRow key={r.id} rel={r} subjectId={id!} />)}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Mention timeline ({mentions.length})</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {mentions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No mentions recorded yet</p>
              ) : (
                mentions.map((m, i) => <MentionRow key={i} mention={m} />)
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
